import { captureUtm } from "@features/form/useUTM"
import { BackgroundRenderer } from "@features/gfx/BackgroundRenderer"
import { PillarTabs } from "@features/pillars/PillarTabs"
import { Header } from "@features/site/Header"
import { MainMenuOverlay } from "@features/site/MainMenuOverlay"
import { useAppState } from "@hooks/useAppState"
import { ClientOnly, RouteDisplay, RouteState, useRoute, useRouterEvents } from "eddev/routing"
import { defineView } from "eddev/views"
import { AnimatePresence, LayoutGroup, motion, useIsPresent } from "motion/react"
import { PropsWithChildren, useEffect, useRef } from "react"

export default defineView("_app", (props) => {
  // Remember UTM parameters, in case the user lands on the site but then eventually uses the contact form
  useEffect(() => {
    captureUtm()
  }, [])

  return (
    <>
      <Header />
      <Page />
      <MainMenuOverlay />
      <PillarTabs />
      <ClientOnly>
        <BackgroundRenderer />
      </ClientOnly>
    </>
  )
})

/**
 * Render the current page, but use AnimatePresence to give components on
 * the page the opportunity to transition in + out via motion initial/animate/exit props.
 */
function Page() {
  const appState = useAppState()
  const route = useRoute()

  return (
    <>
      <motion.div
        data-page-wrap
        animate={appState.menuIsOpen ? { opacity: 0.5, scale: 0.95 } : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="absolute inset-0 z-10 will-change-transform"
      >
        <LayoutGroup id="main">
          <AnimatePresence mode="popLayout">
            <motion.div key={route.id} data-page-wrapper>
              <ScrollShift route={route}>
                <RouteDisplay route={route} />
              </ScrollShift>
            </motion.div>
          </AnimatePresence>
        </LayoutGroup>
      </motion.div>
    </>
  )
}

/**
 * Since our page transitions are async (fading out + fading in at the same time), we need
 * to compensate for the scroll position.
 *
 * When the outgoing page is about to be unrendered (useIsPresent() == false), we quickly:
 * - Position the outgoing page using position:fixed, relative to the scroll viewport
 * - Instantly snap the window scroll to the scroll restoration target
 */
function ScrollShift(props: PropsWithChildren<{ route: RouteState }>) {
  const ref = useRef<HTMLDivElement>(null!)
  const isPresent = useIsPresent()

  useRouterEvents((event) => {
    if (event.type === "navigate:changed") {
      if (!isPresent) {
        const targetTop = event.currentRoute.returnState?.scrollTop ?? 0
        ref.current.style.position = "fixed"
        ref.current.style.top = -document.scrollingElement!.scrollTop + targetTop + "px"
        ref.current.style.left = "0px"
        ref.current.style.right = "0px"
        document.scrollingElement!.scrollTop = targetTop
      }
    }
  })

  return (
    <div ref={ref} style={isPresent ? undefined : {}}>
      {props.children}
    </div>
  )
}
